import { formatDate } from '../../../ui/utils/format-date';

import type { ListSuperadminOrganizationsResponseDtoItemsItemBilling } from '../../../api';

type SuperadminBillingStatus = NonNullable<
  ListSuperadminOrganizationsResponseDtoItemsItemBilling
>['status'];

const STAGE_PALETTE = {
  neutral: '#F0F0F1',
  amber: '#FDECC8',
  blue: '#D3E5EF',
  green: '#DBEDDB',
  coral: '#FFE2DD',
} as const;

export const billingStatusLabel = (status: SuperadminBillingStatus): string => {
  const labels: Record<SuperadminBillingStatus, string> = {
    trialing: 'Trial',
    active: 'Active',
    past_due: 'Past due',
    suspended: 'Suspended',
    cancelled: 'Cancelled',
  };
  return labels[status] ?? status;
};

export const billingStatusFillColor = (status: SuperadminBillingStatus): string => {
  const bg: Record<SuperadminBillingStatus, string> = {
    trialing: STAGE_PALETTE.blue,
    active: STAGE_PALETTE.green,
    past_due: STAGE_PALETTE.amber,
    suspended: STAGE_PALETTE.coral,
    cancelled: STAGE_PALETTE.neutral,
  };
  return bg[status] ?? STAGE_PALETTE.neutral;
};

export const billingPeriodEndLabel = (
  billing: ListSuperadminOrganizationsResponseDtoItemsItemBilling,
): string | null => {
  if (!billing) return null;
  if (billing.status === 'trialing' && billing.trialEndsAt) {
    return `Trial ends ${formatDate(billing.trialEndsAt)}`;
  }
  if (billing.currentPeriodEnd) {
    return `Renews ${formatDate(billing.currentPeriodEnd)}`;
  }
  return null;
};

export const stripeCustomerDashboardUrl = (stripeCustomerId: string): string =>
  `https://dashboard.stripe.com/customers/${stripeCustomerId}`;

export const formatPriceCents = (
  amountCents: number | null | undefined,
  currency: string | null | undefined,
  options: { dropZeroCents?: boolean } = {},
): string => {
  if (amountCents == null) return '';
  // Plan prices are whole dollars: "$499", not "$499.00", where the caller asks for it.
  const wholeDollars = options.dropZeroCents && amountCents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency ?? 'usd').toUpperCase(),
    ...(wholeDollars ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  }).format(amountCents / 100);
};
